import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

// One-time consumption of ?reauth=<token> appended by the Gmail OAuth callback.
// This works around Safari ITP dropping the auth cookie on the cross-site
// redirect back from accounts.google.com. We rebuild the session using a
// short-lived token issued by the backend.
async function consumeReauthFromUrl() {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("reauth");
    if (!token) return null;
    // Strip the reauth param from the URL so it isn't reused / shared.
    url.searchParams.delete("reauth");
    const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : "") + url.hash;
    window.history.replaceState({}, "", cleaned);
    const { data } = await api.post("/auth/exchange", { token });
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      // First try to consume a reauth token from the URL (post-OAuth).
      const reauthUser = await consumeReauthFromUrl();
      if (reauthUser) {
        setUser(reauthUser);
        return;
      }
      await refresh();
    })();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const updateUser = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, error, setError, login, register, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
